import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import { toggleDiagramDetails, toggleModalDiagramSoftDelete, toggleModalDiagramClone, toggleModalDiagramShare, diagramRename, diagramRenameDescription } from '../../../redux/main/main.actions';
import { selectIsModalDiagramDetailsEnabled, selectModalDiagramDetailsData } from '../../../redux/main/main.selectors';

import { trackEvent } from '../../../lib/analytics';

import Modal from '../Modal/Modal';
import './ModalDiagramDetails.scss';

export const ModalDiagramDetails = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    const isOpen = useSelector(selectIsModalDiagramDetailsEnabled);
    const modalData = useSelector(selectModalDiagramDetailsData);

    const [page, setPage] = useState('');

    const inputRef = useRef(null);

    const [title, setTitle] = useState(modalData?.diagram?.title || '');
    const [description, setDescription] = useState(modalData?.diagram?.description || '');

    useEffect(() => {
        if (!modalData?.diagram?.title) return;

        setPage(modalData?.page);

        if (title !== modalData?.diagram?.title) {
            setTitle(modalData?.diagram?.title);
        }

        if (description !== modalData?.diagram?.description) {
            setDescription(modalData?.diagram?.description);
        }

        return () => { };
    }, [modalData]);

    const handleCancel = () => {
        dispatch(toggleDiagramDetails(null));
        setTimeout(() => {
            setPage('');
            setTitle(modalData?.diagram?.title);
            setDescription(modalData?.diagram?.description);
        }, 300);
    };

    const handleTitleBlur = (value) => {
        if (value !== modalData?.diagram?.title) {
            const updatedValue = value || 'Untitled';
            setTitle(updatedValue);
            dispatch(diagramRename(modalData?.diagram?.id, { title: updatedValue }));
        }
    };

    const handleDescriptionBlur = (value) => {
        if (value !== modalData?.diagram?.description) {
            dispatch(diagramRename(modalData?.diagram?.id, { description: value }));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleTitleBlur(title);
            handleCancel();
        }
    };

    const handleDelete = () => {
        trackEvent('app_modal_diagram_details_delete', {
            page: 'app',
            type: 'modals',
        });
        dispatch(toggleModalDiagramSoftDelete(modalData?.page || 'main', modalData?.diagram?.id));
        setTimeout(() => {
            dispatch(toggleDiagramDetails(null));
        }, 30);
    };

    const handleDuplicate = () => {
        trackEvent('app_modal_diagram_details_copy', {
            page: 'app',
            type: 'modals',
        });

        dispatch(toggleModalDiagramClone(modalData?.page || 'main', modalData?.diagram?.id));
        setTimeout(() => {
            dispatch(toggleDiagramDetails(null));
        }, 30);
    };

    const handleShare = () => {
        trackEvent('app_modal_diagram_details_share', {
            page: 'app',
            type: 'modals',
        });
        dispatch(toggleModalDiagramShare(modalData?.page || 'main', modalData?.diagram?.id));
        setTimeout(() => {
            dispatch(toggleDiagramDetails(null));
        }, 30);
    };

    const handleRender = () => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            modalClass={`modal-diagram-details ${page}`}
            onCancel={handleCancel}
            onShouldRender={handleRender}
        >
            {/* <div className="modal-content-header">{`Diagram details`}</div> */}
            <div className="modal-content-body first">
                <input
                    maxLength="1000"
                    ref={inputRef}
                    className='input-name'
                    autoComplete="off"
                    spellCheck="false"
                    type="text"
                    id="diagram-name"
                    placeholder={t('m_diagram_details.name_place')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={(e) => handleTitleBlur(e.target.value)}
                />
                <textarea
                    maxLength="1000"
                    className='input-description'
                    autoComplete="off"
                    spellCheck="false"
                    id="board-description"
                    placeholder={t('m_diagram_details.desc_place')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onBlur={(e) => handleDescriptionBlur(e.target.value)}
                />
            </div>
            <div className="modal-content-body second">
                <button className='button-second btn-delete' onClick={() => handleDelete()}>{t('b.delete')}</button>
                <div className='right-part'>
                    <button className='button-duplicate' onClick={() => handleDuplicate()}>{t('b.duplicate')}</button>
                    <button onClick={() => handleShare()}>{t('b.share')}</button>
                </div>
            </div>
        </Modal >
    );
};

export default ModalDiagramDetails;
